import { Fragment, useState } from "react";
import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  RectangleGroupIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Logo from "../../assets/images/86294Sized_AdVendoo_falt_RK_01.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoCarSportSharp } from "react-icons/io5";
import { MdOutlineScreenshotMonitor, MdAdsClick } from "react-icons/md";
import { IoFastFoodOutline } from "react-icons/io5";
import { baseURL } from "../../api/api";
import { FaUserCircle } from "react-icons/fa";
import { useUser } from "../../context/UserContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const OwnerLayout = ({ children, page, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentView, switchView, canSwitchToCompany, profileDetails } =
    useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSwitchToCompany = () => {
    switchView("company");
    navigate("/companydashboard", { state: { isViewSwitch: true } });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("currentView");
    navigate("/");
  };

  const navigation = [
    {
      name: "Dashboard",
      to: "/ownerdashboard",
      icon: RectangleGroupIcon,
      current: location.pathname === "/dashboard",
    },
    {
      name: "Profile",
      to: "/ownerprofile",
      icon: UserCircleIcon,
      current: location.pathname === "/ownerprofile",
    },
    {
      name: "Mobile Locations",
      to: "/mobileLocations",
      icon: IoCarSportSharp,
      current: location.pathname === "/mobileLocations",
    },
    {
      name: "Fixed Locations",
      to: "/fixedLocations",
      icon: IoFastFoodOutline,
      current:
        location.pathname === "/fixedLocations" ||
        location.pathname === "/fixedLocations",
    },
    {
      name: "Screens",
      to: "/screens",
      icon: MdOutlineScreenshotMonitor,
      current:
        location.pathname === "/screens" || location.pathname === "/screens",
    },
    {
      name: "Infotainment",
      to: "/infotainmentvideos",
      icon: MdAdsClick,
      current:
        location.pathname === "/infotainmentvideos" ||
        location.pathname === "/infotainmentvideos",
    },
  ];

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-40 md:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-14 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-5 w-5 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-dark"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex shrink-0 items-center px-4">
                    <img className="w-full" src={Logo} alt="Your Company" />
                  </div>
                  <div className="mt-5 h-0 flex-1 overflow-y-auto">
                    <nav className="space-y-1 px-2">
                      {navigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.to}
                          className={classNames(
                            item.current
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-500"
                                : "text-gray-400 group-hover:text-gray-500",
                              "mr-4 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-48 md:flex-col z-50">
          <div className="w-64 flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5">
            <div className="flex flex-shrink-0 items-center px-4">
              <img className="w-full" src={Logo} alt="Your Company" />
            </div>
            <div className="mt-5 flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2 pb-4">
                {navigation.map((item) =>
                  !item.children ? (
                    <Link
                      key={item.name}
                      to={item.to}
                      className={classNames(
                        item.current
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                        "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-gray-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "mr-3 flex-shrink-0 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  ) : (
                    <Disclosure as="div" key={item.name} className="space-y-1">
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            className={classNames(
                              item.current
                                ? "bg-gray-100 text-gray-900"
                                : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                              "group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-yellow"
                            )}
                          >
                            <item.icon
                              className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                              aria-hidden="true"
                            />
                            <span className="flex-1">{item.name}</span>
                            <svg
                              className={classNames(
                                open
                                  ? "text-gray-400 rotate-90"
                                  : "text-gray-300",
                                "ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
                              )}
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                d="M6 6L14 10L6 14V6Z"
                                fill="currentColor"
                              />
                            </svg>
                          </Disclosure.Button>
                          <Disclosure.Panel className="space-y-1">
                            {item.children.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.to}
                                className="group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  )
                )}
              </nav>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col md:pl-60">
          <div className="sticky top-0 flex h-16 flex-shrink-0 bg-white shadow z-30">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 justify-between px-3">
              <div className="flex flex-1">
                <h1 className="md:px-5 text-gray-700 font-bold sm:text-sm md:text-lg my-auto">
                  {title}
                </h1>
              </div>
              <div className="ml-4 flex items-center md:ml-6">
                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex max-w-xs items-center rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow focus:ring-offset-2">
                      <span className="sr-only">Open user menu</span>
                      {profileDetails?.profileImage ? (
                        <>
                          <img
                            className="h-9 w-9 rounded-full"
                            src={`${baseURL}/s3/images/${profileDetails?.profileImage}`}
                            alt=""
                          />
                          <span className="text-gray-500 font-semibold text-sm px-3">
                            {profileDetails.firstName} {profileDetails.lastName}
                          </span>
                        </>
                      ) : (
                        <>
                          <FaUserCircle className="text-gray-300 text-2xl" />
                          <span className="text-gray-500 font-semibold text-sm px-2">
                            {profileDetails?.firstName}{" "}
                            {profileDetails?.lastName}
                          </span>
                        </>
                      )}
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right px-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {/* Switch to Company button - only show if user can switch */}
                      {canSwitchToCompany && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={classNames(
                                active
                                  ? "bg-yellow text-black"
                                  : "text-gray-700",
                                "flex items-center w-full px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150"
                              )}
                              onClick={handleSwitchToCompany}
                            >
                              Switch to Advertiser
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      {/* Sign Out button */}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "w-full block px-4 py-2 text-sm text-gray-700 text-left"
                            )}
                            onClick={handleLogout}
                          >
                            Sign Out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50 min-h-screen">{children}</div>
        </div>
      </div>
    </>
  );
};

export default OwnerLayout;
